import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, serverTimestamp, query, orderBy, getDocs, where, limit, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { PlusCircle, TrendingUp, TrendingDown, DollarSign, Wallet, LayoutDashboard, ListPlus, Users, Briefcase } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ComposedChart, Bar, Line } from 'recharts';
import Swal from 'sweetalert2';
import { logAuditAction } from '../api/auditService';
import { fetchSistemaMetrics } from '../api/sistemaBridge';
import { parseMoney } from '../utils/money';
import { applyHistoricalFinancial2026, calculateDynamicFinancialTotals } from '../utils/historicalFinancial2026';
import { emptyMonthlyValues, movementAmountUsd } from '../utils/financialSummary';

import { useAuth } from '../hooks/useAuth';

const FinanceDashboard = () => {
    const { currentUser } = useAuth();
    const [activeTab, setActiveTab] = useState('resumen'); // 'resumen', 'movimientos', 'retiros', 'fondo'
    const [movimientos, setMovimientos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sistemaMetrics, setSistemaMetrics] = useState({});

    const [configEmpresa, setConfigEmpresa] = useState(null);
    const [retirosFondo, setRetirosFondo] = useState([]);
    
    // Form state (Fondo)
    const [montoFondo, setMontoFondo] = useState('');
    const [conceptoFondo, setConceptoFondo] = useState('');

    const [tipoMov, setTipoMov] = useState('ingreso');
    const [categoriaMov, setCategoriaMov] = useState('');
    const [subcategoriaMov, setSubcategoriaMov] = useState('');
    const [montoMov, setMontoMov] = useState('');
    const [conceptoMov, setConceptoMov] = useState('');

    // Form state (Retiros)
    const [socioRetiro, setSocioRetiro] = useState('');
    const [montoRetiro, setMontoRetiro] = useState('');
    const [conceptoRetiro, setConceptoRetiro] = useState('');
    const [metodoRetiro, setMetodoRetiro] = useState('Binance');
    const [editingRetiroId, setEditingRetiroId] = useState(null);

    const [categoriasIngreso, setCategoriasIngreso] = useState([]);
    const [categoriasGasto, setCategoriasGasto] = useState([]);
    const metodosRetiro = ['Binance', 'Zelle', 'Efectivo', 'Zinli'];
    const [socios, setSocios] = useState([]);
    const [cuentas, setCuentas] = useState([]);
    const [cuentasRenovadas, setCuentasRenovadas] = useState([]);


    // Global Filters
    const currentYearDefault = new Date().getFullYear();
    const currentMonthDefault = new Date().getMonth();
    const [filtroAnio, setFiltroAnio] = useState(currentYearDefault);
    const [filtroMes, setFiltroMes] = useState(currentMonthDefault);

    useEffect(() => {
        fetchSistemaMetrics(filtroAnio, filtroMes === -1 ? new Date().getMonth() + 1 : filtroMes + 1)
            .then(setSistemaMetrics)
            .catch(error => console.error('No se pudieron cargar métricas Sistema:', error));
    }, [filtroAnio, filtroMes]);

    useEffect(() => {
        const fechaInicioAño = Timestamp.fromDate(new Date(filtroAnio, 0, 1));
        const q = query(
            collection(db, 'finanzas_movimientos'),
            where('fecha', '>=', fechaInicioAño),
            orderBy('fecha', 'desc'),
            limit(1000)
        );
        const handleRealtimeError = (error) => {
            console.error('Se perdió la conexión en tiempo real:', error);
            Swal.fire('Error', 'Se perdió la conexión en tiempo real', 'error');
        };

        const unsubscribeMovs = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setMovimientos(data);
            setLoading(false);
        }, handleRealtimeError);

        const qCats = query(collection(db, 'config_categorias'), limit(200));
        const unsubscribeCats = onSnapshot(qCats, (snapshot) => {
            const cats = snapshot.docs.map(d => d.data());
            const ing = cats.filter(c => c.tipo === 'ingreso');
            const gas = cats.filter(c => c.tipo === 'gasto');
            setCategoriasIngreso(ing);
            setCategoriasGasto(gas);
        }, handleRealtimeError);

        const qSocios = query(collection(db, 'config_socios'), limit(200));
        const unsubscribeSocios = onSnapshot(qSocios, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSocios(data);
            const activos = data.filter(s => s.estado?.toLowerCase() === 'activo');
            if (activos.length > 0) setSocioRetiro(prev => prev || activos[0].nombre);
        }, handleRealtimeError);

        const unsubEmpresa = onSnapshot(doc(db, 'config_empresa', 'main'), (docSnap) => {
            if (docSnap.exists()) setConfigEmpresa(docSnap.data());
        }, handleRealtimeError);

        const qFondo = query(collection(db, 'finanzas_fondo_retiros'), orderBy('fecha', 'desc'), limit(500));
        const unsubFondo = onSnapshot(qFondo, snap => {
            setRetirosFondo(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        }, handleRealtimeError);

        const qCuentas = query(collection(db, 'inventario_cuentas'), limit(1000));
        const unsubCuentas = onSnapshot(qCuentas, (snapshot) => {
            setCuentas(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, handleRealtimeError);

        const qRenov = query(collection(db, 'cuentas_renovadas'), limit(1000));
        const unsubRenov = onSnapshot(qRenov, (snapshot) => {
            setCuentasRenovadas(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, handleRealtimeError);

        return () => {
            unsubscribeMovs(); unsubscribeCats(); unsubscribeSocios();
            unsubEmpresa(); unsubFondo(); unsubCuentas(); unsubRenov();
        };
    }, [filtroAnio]);

    useEffect(() => {
        const currentList = tipoMov === 'ingreso' ? categoriasIngreso : categoriasGasto;
        setCategoriaMov(prev => {
            const exists = currentList.some(c => c.nombre === prev);
            return exists ? prev : currentList[0]?.nombre || '';
        });
    }, [tipoMov, categoriasIngreso, categoriasGasto]);

    useEffect(() => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth();
        const key = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
        
        if (configEmpresa && configEmpresa.reserva_porcentaje !== undefined) {
            const historico = configEmpresa.historico_porcentajes || {};
            if (historico[key] !== configEmpresa.reserva_porcentaje) {
                updateDoc(doc(db, 'config_empresa', 'main'), {
                    [`historico_porcentajes.${key}`]: configEmpresa.reserva_porcentaje
                }).catch(err => console.error("Error locking percentage:", err));
            }
        }
    }, [configEmpresa]);

    const handleSaveMovimiento = async (e) => {
        e.preventDefault();
        const montoNormalizado = parseMoney(montoMov);
        if (montoNormalizado === null) return Swal.fire('Error', 'El monto debe ser válido. Ej.: 1500,50 o 10.000,05.', 'error');
        if (!conceptoMov) return Swal.fire('Error', 'El concepto es obligatorio', 'error');

        try {
            await addDoc(collection(db, 'finanzas_movimientos'), {
                tipo: tipoMov,
                categoria: categoriaMov,
                subcategoria: subcategoriaMov || null,
                monto: montoNormalizado,
                concepto: conceptoMov,
                fecha: serverTimestamp(),
            });
            logAuditAction({
                userId: currentUser?.uid || currentUser?.id,
                userName: currentUser?.name || currentUser?.email,
                modulo: 'Finanzas',
                accion: 'Creación de Movimiento',
                descripcion: `Movimiento de tipo ${tipoMov} por $${montoMov} (${conceptoMov})`
            });
            Swal.fire('Éxito', 'Movimiento registrado correctamente', 'success');
            
            // LOGIC FOR ALMACEN
            const catLower = categoriaMov.toLowerCase();
            if (tipoMov === 'gasto' && (catLower.includes('inventario') || catLower.includes('compras de mercancía') || catLower.includes('compras de mercancia'))) {
                const { isConfirmed } = await Swal.fire({
                    title: 'Aviso de Stock',
                    text: '¿Deseas sumar estas unidades compradas al Almacén Digital?',
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: 'Sí, sumar al stock',
                    cancelButtonText: 'No'
                });

                if (isConfirmed) {
                    const { value: formValues } = await Swal.fire({
                        title: 'Datos de la Compra',
                        html:
                            `<input id="swal-unidades" class="swal2-input" placeholder="Unidades compradas" type="number" min="1">` +
                            `<input id="swal-precio" class="swal2-input" placeholder="Precio Venta Sugerido ($)" type="number" step="0.01">` +
                            `<input id="swal-prov" class="swal2-input" placeholder="Proveedor (Opcional)">`,
                        focusConfirm: false,
                        preConfirm: () => {
                            const unidades = document.getElementById('swal-unidades').value;
                            const precio = document.getElementById('swal-precio').value;
                            const prov = document.getElementById('swal-prov').value;
                            if (!unidades) {
                                Swal.showValidationMessage('Las unidades son obligatorias');
                                return false;
                            }
                            return { unidades: Number(unidades), precio: Number(precio || 0), prov };
                        }
                    });

                    if (formValues) {
                        const plataforma = subcategoriaMov || 'Desconocida';
                        const qCheck = query(collection(db, 'almacen_stock'), where('plataforma', '==', plataforma));
                        const querySnapshot = await getDocs(qCheck);
                        
                        const costoTotalNuevaCompra = montoNormalizado;
                        const nuevasUnidades = formValues.unidades;

                        if (!querySnapshot.empty) {
                            const docRefSnap = querySnapshot.docs[0];
                            const currentData = docRefSnap.data();
                            const currentUnidades = currentData.unidades || 0;
                            const currentCostoPromedio = currentData.costo_promedio || 0;
                            const costoTotalAnterior = currentUnidades * currentCostoPromedio;
                            
                            const nuevoCostoPromedio = (costoTotalAnterior + costoTotalNuevaCompra) / (currentUnidades + nuevasUnidades);
                            
                            await updateDoc(doc(db, 'almacen_stock', docRefSnap.id), {
                                unidades: currentUnidades + nuevasUnidades,
                                costo_promedio: nuevoCostoPromedio,
                                precio_sugerido: formValues.precio || currentData.precio_sugerido,
                                proveedor: formValues.prov || currentData.proveedor
                            });
                        } else {
                            await addDoc(collection(db, 'almacen_stock'), {
                                plataforma: plataforma,
                                unidades: nuevasUnidades,
                                costo_promedio: costoTotalNuevaCompra / nuevasUnidades,
                                precio_sugerido: formValues.precio,
                                proveedor: formValues.prov
                            });
                        }
                        Swal.fire('Stock Actualizado', `Se sumaron ${nuevasUnidades} unidades de ${plataforma} al almacén.`, 'success');
                    }
                }
            }

            setMontoMov(''); setConceptoMov(''); 
            // Keep current category and subcategory settings instead of resetting to default
        } catch (error) {
            Swal.fire('Error', 'Hubo un problema al guardar', 'error');
        }
    };

    const handleSaveRetiro = async (e) => {
        e.preventDefault();
        const montoNormalizado = parseMoney(montoRetiro);
        if (montoNormalizado === null) return Swal.fire('Error', 'El monto debe ser válido. Ej.: 1500,50 o 10.000,05.', 'error');
        if (!conceptoRetiro) return Swal.fire('Error', 'El concepto es obligatorio', 'error');

        try {
            if (editingRetiroId) {
                await updateDoc(doc(db, 'finanzas_movimientos', editingRetiroId), {
                    socio: socioRetiro,
                    categoria: metodoRetiro,
                    monto: montoNormalizado,
                    concepto: conceptoRetiro,
                });
                logAuditAction({
                    userId: currentUser?.uid || currentUser?.id,
                    userName: currentUser?.name || currentUser?.email,
                    modulo: 'Finanzas',
                    accion: 'Edición de Retiro',
                    descripcion: `Retiro editado para socio ${socioRetiro} por $${montoRetiro} (${conceptoRetiro})`
                });
                Swal.fire({ title: 'Actualizado', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            } else {
                await addDoc(collection(db, 'finanzas_movimientos'), {
                    tipo: 'retiro',
                    socio: socioRetiro,
                    categoria: metodoRetiro,
                    monto: montoNormalizado,
                    concepto: conceptoRetiro,
                    fecha: serverTimestamp(),
                });
                logAuditAction({
                    userId: currentUser?.uid || currentUser?.id,
                    userName: currentUser?.name || currentUser?.email,
                    modulo: 'Finanzas',
                    accion: 'Creación de Retiro',
                    descripcion: `Retiro registrado para socio ${socioRetiro} por $${montoRetiro} (${conceptoRetiro})`
                });
                Swal.fire({ title: 'Registrado', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            }
            setMontoRetiro(''); setConceptoRetiro(''); setEditingRetiroId(null);
        } catch (error) {
            Swal.fire('Error', 'Hubo un problema al guardar', 'error');
        }
    };

    const handleEditRetiro = (mov) => {
        setEditingRetiroId(mov.id);
        setSocioRetiro(mov.socio);
        setMontoRetiro(mov.monto.toString());
        setConceptoRetiro(mov.concepto);
        setMetodoRetiro(mov.categoria);
        // Scroll to form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteRetiro = async (id) => {
        if (window.confirm('¿Estás seguro de eliminar este retiro? Esto actualizará los saldos.')) {
            await deleteDoc(doc(db, 'finanzas_movimientos', id));
            logAuditAction({
                userId: currentUser?.uid || currentUser?.id,
                userName: currentUser?.name || currentUser?.email,
                modulo: 'Finanzas',
                accion: 'Eliminación de Movimiento/Retiro',
                descripcion: `Movimiento eliminado ID: ${id}`
            });
        }
    };


    const getMonthAndYear = (fechaRaw) => {
        if (!fechaRaw) return { month: -1, year: -1 };
        const date = fechaRaw.toDate ? fechaRaw.toDate() : new Date(fechaRaw);
        return { month: date.getMonth(), year: date.getFullYear() };
    };

    const getFondoPorcentaje = useCallback((year, month) => {
        const key = `${year}-${String(month + 1).padStart(2, '0')}`;
        const historico = configEmpresa?.historico_porcentajes || {};
        if (historico[key] !== undefined) return Number(historico[key]);
        
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth();
        if (year === currentYear && month === currentMonth) {
            return Number(configEmpresa?.reserva_porcentaje || 0);
        }
        return 0;
    }, [configEmpresa]);

    const stats = useMemo(() => {
        let ingresos = 0; let gastos = 0; let totalRetiros = 0; let retirosPorSocio = {};
        let utilidadesPorMes = {};
        let fondoMes = 0;

        movimientos.forEach(mov => {
            const { month, year } = getMonthAndYear(mov.fecha);
            const key = `${year}-${month}`;
            if (!utilidadesPorMes[key]) utilidadesPorMes[key] = 0;
            
            const monto = Number(mov.monto) || 0;
            if (mov.tipo === 'ingreso') {
                utilidadesPorMes[key] += monto;
                if (year === filtroAnio && (filtroMes === -1 || month === filtroMes)) ingresos += monto;
            }
            if (mov.tipo === 'gasto') {
                utilidadesPorMes[key] -= monto;
                if (year === filtroAnio && (filtroMes === -1 || month === filtroMes)) gastos += monto;
            }
            if (mov.tipo === 'retiro') {
                if (year === filtroAnio && (filtroMes === -1 || month === filtroMes)) {
                    totalRetiros += monto;
                    retirosPorSocio[mov.socio] = (retirosPorSocio[mov.socio] || 0) + monto;
                }
            }
        });

        // Fondo histórico total
        let fondoTotalAsignado = 0;
        Object.keys(utilidadesPorMes).forEach(key => {
            const [y, m] = key.split('-').map(Number);
            const utilidad = utilidadesPorMes[key];
            if (utilidad > 0) {
                const pct = getFondoPorcentaje(y, m);
                fondoTotalAsignado += utilidad * (pct / 100);
            }
        });

        // Fondo del mes seleccionado
        if (filtroMes === -1) {
            for(let m = 0; m < 12; m++) {
                const key = `${filtroAnio}-${m}`;
                const ut = utilidadesPorMes[key] || 0;
                if (ut > 0) {
                    fondoMes += ut * (getFondoPorcentaje(filtroAnio, m) / 100);
                }
            }
        } else {
            const key = `${filtroAnio}-${filtroMes}`;
            const ut = utilidadesPorMes[key] || 0;
            if (ut > 0) {
                fondoMes += ut * (getFondoPorcentaje(filtroAnio, filtroMes) / 100);
            }
        }

        let gastosInventario = 0;
        let gastosNomina = 0;

        movimientos.forEach(mov => {
            const { month, year } = getMonthAndYear(mov.fecha);
            const monto = Number(mov.monto) || 0;
            if (mov.tipo === 'gasto' && year === filtroAnio && (filtroMes === -1 || month === filtroMes)) {
                const catLower = (mov.categoria || '').toLowerCase();
                if (catLower.includes('inventario') || catLower.includes('compras') || catLower.includes('mercancía') || catLower.includes('mercancia')) {
                    gastosInventario += monto;
                }
                if (catLower.includes('personal') || catLower.includes('nomina') || catLower.includes('nómina')) {
                    gastosNomina += monto;
                }
            }
        });

        const margenNeto = ingresos - (gastosInventario + gastosNomina + Number(sistemaMetrics.gastos_sistema_mes || 0));

        ingresos += Number(sistemaMetrics.ingresos_mes || 0);
        gastos += Number(sistemaMetrics.gastos_sistema_mes || 0);
        const utilidadBruta = ingresos - gastos;

        const gananciaARepartir = utilidadBruta > 0 ? utilidadBruta - fondoMes : utilidadBruta;
        const utilidad = gananciaARepartir - totalRetiros;

        const totalRetirosFondo = (retirosFondo || []).reduce((acc, curr) => acc + Number(curr.monto), 0);
        const saldoFondo = fondoTotalAsignado - totalRetirosFondo;

        let proyeccionVencimientos = 0;
        const now = Date.now();
        const next30Days = now + (30 * 24 * 60 * 60 * 1000);

        const cuentasExpiring = cuentas.filter(c => {
            if (!c.fecha_vencimiento) return false;
            const fVenc = typeof c.fecha_vencimiento === 'number' ? c.fecha_vencimiento : new Date(c.fecha_vencimiento).getTime();
            return fVenc >= now && fVenc <= next30Days && c.estado?.toLowerCase() !== 'vendida' && c.estado?.toLowerCase() !== 'desechada';
        });

        const costPerService = {};
        cuentasRenovadas.forEach(r => {
            if (!r.servicio) return;
            const sName = r.servicio.toUpperCase();
            if (!costPerService[sName]) costPerService[sName] = [];
            costPerService[sName].push(Number(r.costo || 0));
        });

        const avgCostPerService = {};
        Object.keys(costPerService).forEach(s => {
            const arr = costPerService[s];
            const sum = arr.reduce((a, b) => a + b, 0);
            avgCostPerService[s] = sum / arr.length;
        });

        cuentasExpiring.forEach(c => {
            const sName = (c.servicio || '').toUpperCase();
            const cost = avgCostPerService[sName] !== undefined ? avgCostPerService[sName] : 3.00;
            proyeccionVencimientos += cost;
        });

        proyeccionVencimientos = Number(sistemaMetrics.proximos_vencimientos_30d || proyeccionVencimientos);
        return { ingresos, gastos, retirosPorSocio, totalRetiros, utilidad, utilidadBruta, fondoMes, gananciaARepartir, fondoTotalAsignado, totalRetirosFondo, saldoFondo, margenNeto, proyeccionVencimientos };
    }, [movimientos, filtroMes, filtroAnio, retirosFondo, cuentas, cuentasRenovadas, getFondoPorcentaje, sistemaMetrics]);
    const chartGastosData = useMemo(() => {
        const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        const data = months.map(name => ({ name, Cuentas: 0, Empleados: 0, Licencias: 0, Otros: 0, Ingresos: 0, Utilidad: 0, Ratio: 0 }));

        const getMonthAndYear = (fechaRaw) => {
            if (!fechaRaw) return { month: -1, year: -1 };
            let d;
            if (fechaRaw.toDate) d = fechaRaw.toDate();
            else d = new Date(fechaRaw);
            return { month: d.getMonth(), year: d.getFullYear() };
        };

        movimientos.forEach(mov => {
            const { month, year } = getMonthAndYear(mov.created_at || mov.fecha || mov.date);
            if (year === filtroAnio && month >= 0 && month <= 11) {
                const amount = Number(mov.amount || mov.monto || mov.total || 0);
                if (mov.tipo === 'gasto') {
                    const catLower = (mov.categoria || '').toLowerCase();
                    const subcatLower = (mov.subcategoria || '').toLowerCase();
                    
                    if (catLower.includes('cuenta') || catLower.includes('inventario') || subcatLower.includes('cuenta')) {
                        data[month].Cuentas += amount;
                    } else if (catLower.includes('personal') || catLower.includes('nomina') || catLower.includes('nómina') || subcatLower.includes('nomina')) {
                        data[month].Empleados += amount;
                    } else if (catLower.includes('licencia') || catLower.includes('software') || subcatLower.includes('licencia')) {
                        data[month].Licencias += amount;
                    } else {
                        data[month].Otros += amount;
                    }
                } else if (mov.tipo === 'ingreso') {
                    data[month].Ingresos += amount;
                }
            }
        });

        data.forEach(item => {
            const totalGasto = item.Cuentas + item.Empleados + item.Licencias + item.Otros;
            item.Utilidad = item.Ingresos - totalGasto;
            item.Ratio = item.Ingresos > 0 ? Number(((totalGasto / item.Ingresos) * 100).toFixed(1)) : 0;
        });

        return data;
    }, [movimientos, filtroAnio]);
    const tableData = useMemo(() => {
        const result = { ingresos: { 'Ingresos (Ventas y Renovaciones)': emptyMonthlyValues() }, gastos: { Cuentas: emptyMonthlyValues(), Empleados: emptyMonthlyValues(), Licencias: emptyMonthlyValues(), Publicidad: emptyMonthlyValues(), 'Retiro Fondo de Reserva': emptyMonthlyValues(), Membresías: emptyMonthlyValues(), Otros: emptyMonthlyValues() }, retiros: {}, totalIngresos: emptyMonthlyValues(), totalGastos: emptyMonthlyValues(), totalRetiros: emptyMonthlyValues(), utilidadNeta: emptyMonthlyValues(), fondoReserva: emptyMonthlyValues(), fondoPublicidad: emptyMonthlyValues() };
        const expenseCategory = mov => { const value=`${mov.categoria || ''} ${mov.subcategoria || ''}`.toLowerCase(); if(value.includes('cuenta')||value.includes('inventario'))return 'Cuentas'; if(value.includes('emple')||value.includes('personal')||value.includes('nomina')||value.includes('nómina'))return 'Empleados'; if(value.includes('licencia')||value.includes('software'))return 'Licencias'; if(value.includes('public'))return 'Publicidad'; if(value.includes('retiro')&&value.includes('fondo'))return 'Retiro Fondo de Reserva'; if(value.includes('membres'))return 'Membresías'; return 'Otros'; };
        movimientos.forEach(mov => { const {month,year}=getMonthAndYear(mov.created_at||mov.fecha||mov.date); if(year!==filtroAnio||month<0||month>11)return; const monto=movementAmountUsd(mov,sistemaMetrics.tasa_usd); if(mov.tipo==='ingreso')result.ingresos['Ingresos (Ventas y Renovaciones)'][month]+=monto; else if(mov.tipo==='gasto')result.gastos[expenseCategory(mov)][month]+=monto; else if(mov.tipo==='retiro'){if(!result.retiros[mov.socio])result.retiros[mov.socio]=emptyMonthlyValues();result.retiros[mov.socio][month]+=monto;result.totalRetiros[month]+=monto;} });
        const ingresosSistema=sistemaMetrics.series_anuales?.ingresos||[], gastosSistema=sistemaMetrics.series_anuales?.gastos||[]; for(let month=0;month<12;month+=1){result.ingresos['Ingresos (Ventas y Renovaciones)'][month]+=Number(ingresosSistema[month]||0);result.gastos.Cuentas[month]+=Number(gastosSistema[month]||0);}
        calculateDynamicFinancialTotals(result); applyHistoricalFinancial2026(result,filtroAnio); if(filtroAnio===2026)calculateDynamicFinancialTotals(result,10); return result;
    }, [movimientos, filtroAnio, sistemaMetrics]);

    const handleSaveRetiroFondo = async (e) => {
        e.preventDefault();
        const montoNormalizado = parseMoney(montoFondo);
        if (montoNormalizado === null) return Swal.fire('Error', 'El monto debe ser válido. Ej.: 1500,50 o 10.000,05.', 'error');
        if (!conceptoFondo) return Swal.fire('Error', 'El concepto es obligatorio', 'error');
        if (montoNormalizado > stats.saldoFondo) return Swal.fire('Error', 'Saldo insuficiente en el fondo', 'error');

        try {
            await addDoc(collection(db, 'finanzas_fondo_retiros'), {
                monto: montoNormalizado,
                concepto: conceptoFondo,
                fecha: serverTimestamp(),
            });
            Swal.fire({ title: 'Retiro del Fondo Registrado', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            setMontoFondo(''); setConceptoFondo('');
        } catch (error) {
            Swal.fire('Error', 'Hubo un problema al guardar', 'error');
        }
    };

    const renderGraficasFinancieras = () => {
        const CustomTooltip = ({ active, payload, label }) => {
            if (active && payload && payload.length) {
                const data = payload[0].payload;
                const umbral = configEmpresa?.umbral_gasto_saludable !== undefined ? configEmpresa.umbral_gasto_saludable : 60;
                const ratio = data.Ratio;
                
                let healthLabel = 'Óptima';
                let colorHex = '#10b981';
                if (ratio > umbral + 10) {
                    healthLabel = 'Crítica';
                    colorHex = '#ef4444';
                } else if (ratio > umbral) {
                    healthLabel = 'En Observación';
                    colorHex = '#f59e0b';
                }

                return (
                    <div className="bg-slate-800 p-4 border border-slate-700 rounded-lg shadow-xl text-xs space-y-1">
                        <p className="text-slate-400 font-bold mb-1">{label}</p>
                        {payload.map((entry, index) => (
                            <p key={index} style={{ color: entry.color }}>
                                {entry.name}: {entry.name === 'Ratio' ? `${entry.value}%` : `$${entry.value.toLocaleString()}`}
                            </p>
                        ))}
                        <p style={{ color: colorHex }} className="font-bold border-t border-slate-700/50 pt-1 mt-1">
                            Salud Financiera: {healthLabel} ({ratio}%)
                        </p>
                    </div>
                );
            }
            return null;
        };

        return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 my-8 animate-in fade-in duration-500">
                {/* Gráfico 1: Composición de Gastos */}
                <div className="bg-slate-800/80 backdrop-blur-md rounded-2xl p-5 border border-slate-700/50 shadow-xl">
                    <h4 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                        <TrendingDown className="text-red-400" size={18} /> Composición de Gastos ({new Date().getFullYear()})
                    </h4>
                    <div className="h-[280px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartGastosData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} />
                                <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={v => `$${v}`} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                                <Area type="monotone" dataKey="Cuentas" stackId="1" stroke="#f43f5e" fill="#f43f5e" opacity={0.7} />
                                <Area type="monotone" dataKey="Empleados" stackId="1" stroke="#3b82f6" fill="#3b82f6" opacity={0.7} />
                                <Area type="monotone" dataKey="Licencias" stackId="1" stroke="#a855f7" fill="#a855f7" opacity={0.7} />
                                <Area type="monotone" dataKey="Otros" stackId="1" stroke="#64748b" fill="#64748b" opacity={0.7} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Gráfico 2: Eficiencia de Operaciones */}
                <div className="bg-slate-800/80 backdrop-blur-md rounded-2xl p-5 border border-slate-700/50 shadow-xl">
                    <h4 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                        <TrendingUp className="text-emerald-400" size={18} /> Evolución Financiera ({new Date().getFullYear()})
                    </h4>
                    <div className="h-[280px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={chartGastosData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} />
                                <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={v => `$${v}`} />
                                <YAxis yAxisId="right" orientation="right" stroke="#64748b" fontSize={11} tickFormatter={v => `${v}%`} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend iconSize={10} wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                                <Bar dataKey="Ingresos" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={30} />
                                <Bar dataKey="Utilidad" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={30} />
                                <Line yAxisId="right" type="monotone" dataKey="Ratio" stroke="#f59e0b" strokeWidth={3} name="Ratio" dot={{ r: 4, fill: '#f59e0b' }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        );
    };

    const formatDate = (fechaRaw) => {
        if (!fechaRaw) return 'N/A';
        const date = fechaRaw.toDate ? fechaRaw.toDate() : new Date(fechaRaw);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    };

    return (
        <div className="min-h-screen text-slate-100 font-sans">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight m-0">Gestión Financiera</h1>
                    <p className="text-sm text-slate-400 mt-1">Gestión de ingresos, gastos y retiros de socios</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">

                    <select value={filtroAnio} onChange={(e) => setFiltroAnio(Number(e.target.value))} className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-indigo-500">
                        {Array.from({ length: Math.max(2, currentYearDefault - 2026 + 2) }, (_, index) => 2026 + index).map(year => <option key={year} value={year}>{year}</option>)}
                    </select>
                    <select value={filtroMes} onChange={(e) => setFiltroMes(Number(e.target.value))} className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-indigo-500">
                        <option value={-1}>Vista Anual</option>
                        {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((mes, idx) => (
                            <option key={idx} value={idx}>{mes}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tabs Header */}
            <div className="flex border-b border-slate-700/50 mb-6 gap-2 overflow-x-auto scrollbar-hide flex-nowrap whitespace-nowrap">
                <button
                    onClick={() => setActiveTab('resumen')}
                    className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 font-medium transition-colors border-b-2 ${activeTab === 'resumen' ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}
                >
                    <LayoutDashboard size={18} /> Resumen General
                </button>
                <button
                    onClick={() => setActiveTab('movimientos')}
                    className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 font-medium transition-colors border-b-2 ${activeTab === 'movimientos' ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}
                >
                    <ListPlus size={18} /> Movimientos
                </button>
                <button
                    onClick={() => setActiveTab('retiros')}
                    className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 font-medium transition-colors border-b-2 ${activeTab === 'retiros' ? 'border-amber-500 text-amber-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}
                >
                    <Wallet size={18} /> Retiros
                </button>
                <button
                    onClick={() => setActiveTab('fondo')}
                    className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 font-medium transition-colors border-b-2 ${activeTab === 'fondo' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}
                >
                    <Briefcase size={18} /> Fondo de Reserva
                </button>
            </div>

            {/* TAB: RESUMEN */}
            {activeTab === 'resumen' && (
                <div className="space-y-8 animate-in fade-in duration-300">
                    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-6">
                        <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-slate-700/50 shadow-xl">
                            <div className="flex justify-between items-start mb-4">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Ingresos (Mes)</span>
                                <div className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center"><TrendingUp size={20} /></div>
                            </div>
                            <div className="text-3xl font-bold text-white">{loading ? '...' : `$${stats.ingresos.toLocaleString(undefined, {minimumFractionDigits: 2})}`}</div>
                        </div>
                        <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-slate-700/50 shadow-xl">
                            <div className="flex justify-between items-start mb-4">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Gastos (Mes)</span>
                                <div className="w-10 h-10 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center"><TrendingDown size={20} /></div>
                            </div>
                            <div className="text-3xl font-bold text-white">{loading ? '...' : `$${stats.gastos.toLocaleString(undefined, {minimumFractionDigits: 2})}`}</div>
                        </div>
                        <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
                            <div className="flex justify-between items-start mb-4 relative z-10">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Aporte al Fondo (Mes)</span>
                                <div className="w-10 h-10 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center"><Briefcase size={20} /></div>
                            </div>
                            <div className="text-3xl font-bold text-cyan-400 relative z-10">{loading ? '...' : `$${stats.fondoMes.toLocaleString(undefined, {minimumFractionDigits: 2})}`}</div>
                            <div className="text-xs text-slate-400 mt-2 relative z-10">Fondo de Reserva Actual</div>
                        </div>
                        <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
                            <div className="flex justify-between items-start mb-4 relative z-10">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Ganancia a Repartir</span>
                                <div className="w-10 h-10 rounded-full bg-indigo-500/20 text-indigo-400 flex items-center justify-center"><DollarSign size={20} /></div>
                            </div>
                            <div className="text-3xl font-bold text-white relative z-10">{loading ? '...' : `$${stats.gananciaARepartir.toLocaleString(undefined, {minimumFractionDigits: 2})}`}</div>
                            <div className="text-xs text-indigo-400 mt-2 relative z-10">Menos Aporte de Fondo</div>
                        </div>
                        <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
                            <div className="flex justify-between items-start mb-4 relative z-10">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Margen Neto Real</span>
                                <div className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center"><TrendingUp size={20} /></div>
                            </div>
                            <div className="text-3xl font-bold text-emerald-400 relative z-10">${loading ? '...' : stats.margenNeto.toLocaleString(undefined, {minimumFractionDigits: 2})}</div>
                            <div className="text-xs text-slate-400 mt-2 relative z-10">Ingresos - (Personal + Inventario)</div>
                        </div>
                        <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
                            <div className="flex justify-between items-start mb-4 relative z-10">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Próximos Vencimientos</span>
                                <div className="w-10 h-10 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center"><DollarSign size={20} /></div>
                            </div>
                            <div className="text-3xl font-bold text-amber-400 relative z-10">${loading ? '...' : stats.proyeccionVencimientos.toLocaleString(undefined, {minimumFractionDigits: 2})}</div>
                            <div className="text-xs text-slate-400 mt-2 relative z-10">Monto estimado para renovaciones en {(() => {
                                const monthNames = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
                                const nextMonthIdx = (filtroMes === -1 ? (new Date().getMonth() + 1) : filtroMes + 1) % 12;
                                return monthNames[nextMonthIdx];
                            })()}</div>
                        </div>
                    </div>

                    {renderGraficasFinancieras()}

                    <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden">
                        <div className="p-5 border-b border-slate-700/50">
                            <h3 className="text-lg font-bold text-white">Resumen Financiero Detallado ({filtroAnio})</h3>
                        </div>
                        <div className="overflow-x-auto whitespace-nowrap">
                            <table className="w-full text-sm text-left text-slate-300">
                                <thead className="bg-slate-900/50 text-slate-400 uppercase text-xs">
                                    <tr>
                                        <th className="px-6 py-4 font-semibold">Concepto / Categoría</th>
                                        <th className="px-4 py-4 font-semibold">Ene</th><th className="px-4 py-4 font-semibold">Feb</th><th className="px-4 py-4 font-semibold">Mar</th>
                                        <th className="px-4 py-4 font-semibold">Abr</th><th className="px-4 py-4 font-semibold">May</th><th className="px-4 py-4 font-semibold">Jun</th>
                                        <th className="px-4 py-4 font-semibold">Jul</th><th className="px-4 py-4 font-semibold">Ago</th><th className="px-4 py-4 font-semibold">Sep</th>
                                        <th className="px-4 py-4 font-semibold">Oct</th><th className="px-4 py-4 font-semibold">Nov</th><th className="px-4 py-4 font-semibold">Dic</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/50">
                                    {/* Ingresos */}
                                    <tr className="bg-slate-800/40"><td colSpan="13" className="px-6 py-3 font-bold text-emerald-400 uppercase tracking-wider text-xs">INGRESOS</td></tr>
                                    {['Ingresos (Ventas y Renovaciones)'].map(cat => (
                                        <tr key={cat} className="hover:bg-slate-700/30">
                                            <td className="px-6 py-3">{cat}</td>
                                            {tableData.ingresos[cat].map((val, idx) => <td key={idx} className="px-4 py-3">${val.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>)}
                                        </tr>
                                    ))}
                                    <tr className="bg-slate-900/30 font-bold border-b border-slate-700">
                                        <td className="px-6 py-4 text-white">TOTAL INGRESOS</td>
                                        {tableData.totalIngresos.map((val, idx) => <td key={idx} className="px-4 py-4 text-emerald-400">${val.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>)}
                                    </tr>
                                    {/* Gastos */}
                                    <tr className="bg-slate-800/40"><td colSpan="13" className="px-6 py-3 font-bold text-red-400 uppercase tracking-wider text-xs">GASTOS OPERATIVOS</td></tr>
                                    {['Cuentas', 'Empleados', 'Licencias', 'Publicidad', 'Retiro Fondo de Reserva', 'Membresías', 'Otros'].map(cat => (
                                        <tr key={cat} className="hover:bg-slate-700/30">
                                            <td className="px-6 py-3">{cat}</td>
                                            {(tableData.gastos[cat] || Array(12).fill(0)).map((val, idx) => <td key={idx} className="px-4 py-3">${val.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>)}
                                        </tr>
                                    ))}
                                    <tr className="bg-slate-900/30 font-bold border-b border-slate-700">
                                        <td className="px-6 py-4 text-white">TOTAL GASTOS</td>
                                        {tableData.totalGastos.map((val, idx) => <td key={idx} className="px-4 py-4 text-red-400">${val.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>)}
                                    </tr>
                                    {/* Retiros Socios */}
                                    <tr className="bg-slate-800/40"><td colSpan="13" className="px-6 py-3 font-bold text-amber-400 uppercase tracking-wider text-xs">RETIROS SOCIOS</td></tr>
                                    {Object.keys(tableData.retiros).map(socio => (
                                        <tr key={socio} className="hover:bg-slate-700/30">
                                            <td className="px-6 py-3">{socio}</td>
                                            {tableData.retiros[socio].map((val, idx) => <td key={idx} className="px-4 py-3 text-amber-200/70">${val.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>)}
                                        </tr>
                                    ))}
                                    <tr className="bg-slate-900/30 font-bold border-b border-slate-700">
                                        <td className="px-6 py-4 text-white">TOTAL RETIROS</td>
                                        {tableData.totalRetiros.map((val, idx) => <td key={idx} className="px-4 py-4 text-amber-400">${val.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>)}
                                    </tr>
                                    <tr className="bg-cyan-900/20 font-bold border-t border-cyan-500/30"><td className="px-6 py-4 text-cyan-300">FONDO DE RESERVA</td>{tableData.fondoReserva.map((val, idx) => <td key={idx} className="px-4 py-4 text-cyan-300">${val.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>)}</tr>
                                    <tr className="bg-fuchsia-900/20 font-bold border-t border-fuchsia-500/30"><td className="px-6 py-4 text-fuchsia-300">FONDO DE PUBLICIDAD</td>{tableData.fondoPublicidad.map((val, idx) => <td key={idx} className="px-4 py-4 text-fuchsia-300">${val.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>)}</tr>
                                    <tr className="bg-indigo-900/20 font-bold border-t border-indigo-500/30"><td className="px-6 py-5 text-indigo-300">UTILIDAD NETA</td>{tableData.utilidadNeta.map((val, idx) => <td key={idx} className="px-4 py-5 text-indigo-400">${val.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>)}</tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB: MOVIMIENTOS */}
            {activeTab === 'movimientos' && (
                <div className="space-y-6 animate-in fade-in duration-300">
                    <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl p-6">
                        <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2"><PlusCircle size={20} className="text-emerald-400" /> Registrar Movimiento</h3>
                        {(() => {
                            const availableCategories = tipoMov === 'ingreso' ? categoriasIngreso : categoriasGasto;
                            const selectedCategoryObj = availableCategories.find(c => c.nombre === categoriaMov);
                            const availableSubcategories = selectedCategoryObj?.subcategorias || [];
                            return (
                        <form onSubmit={handleSaveMovimiento} className="grid grid-cols-1 md:grid-cols-6 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">Tipo</label>
                                <select value={tipoMov} onChange={(e) => { 
                                    const newTipo = e.target.value;
                                    setTipoMov(newTipo); 
                                    const newAvailable = newTipo === 'ingreso' ? categoriasIngreso : categoriasGasto;
                                    const defaultCat = newAvailable[0] || null;
                                    setCategoriaMov(defaultCat?.nombre || '');
                                    setSubcategoriaMov(defaultCat?.subcategorias?.[0] || '');
                                }} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors">
                                    <option value="ingreso">Ingreso</option>
                                    <option value="gasto">Gasto</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">Categoría</label>
                                <select value={categoriaMov} onChange={(e) => {
                                    setCategoriaMov(e.target.value);
                                    const catObj = availableCategories.find(c => c.nombre === e.target.value);
                                    setSubcategoriaMov(catObj?.subcategorias?.[0] || '');
                                }} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors">
                                    {availableCategories.length === 0 && <option value="" disabled>Contacte al administrador para configurar categorías</option>}
                                    {availableCategories.map(c => <option key={c.nombre} value={c.nombre}>{c.nombre}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">Subcategoría</label>
                                <select value={subcategoriaMov} onChange={(e) => setSubcategoriaMov(e.target.value)} disabled={availableSubcategories.length === 0} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors disabled:opacity-50">
                                    {availableSubcategories.length === 0 && <option value="">N/A</option>}
                                    {availableSubcategories.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">Monto ($)</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><DollarSign size={16} className="text-slate-500" /></div>
                                    <input type="text" inputMode="decimal" value={montoMov} onChange={(e) => setMontoMov(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors placeholder-slate-600" placeholder="Ej. 10.000,05" required />
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-slate-400 mb-2">Concepto</label>
                                <div className="flex gap-2">
                                    <input type="text" value={conceptoMov} onChange={(e) => setConceptoMov(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors placeholder-slate-600" placeholder="Descripción breve" required />
                                    <button type="submit" className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-3 rounded-xl font-medium transition-colors whitespace-nowrap shadow-lg shadow-emerald-500/20">Guardar</button>
                                </div>
                            </div>
                        </form>
                        );})()}
                    </div>

                    <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden">
                        <div className="p-5 border-b border-slate-700/50"><h3 className="text-lg font-bold text-white">Historial de Movimientos</h3></div>
                        <div className="overflow-x-auto max-h-[400px]">
                            <table className="w-full text-sm text-left text-slate-300">
                                <thead className="bg-slate-900/50 text-slate-400 uppercase text-xs sticky top-0">
                                    <tr><th className="px-6 py-4">Fecha</th><th className="px-6 py-4">Tipo</th><th className="px-6 py-4">Categoría / Sub</th><th className="px-6 py-4">Concepto</th><th className="px-6 py-4">Monto</th></tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/50">
                                    {movimientos.filter(m => m.tipo !== 'retiro').map(m => (
                                        <tr key={m.id} className="hover:bg-slate-700/30">
                                            <td className="px-6 py-4 text-xs text-slate-400">{formatDate(m.fecha)}</td>
                                            <td className="px-6 py-4">{m.tipo === 'ingreso' ? <span className="text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-md text-xs font-bold uppercase">Ingreso</span> : <span className="text-red-400 bg-red-500/10 px-2 py-1 rounded-md text-xs font-bold uppercase">Gasto</span>}</td>
                                            <td className="px-6 py-4 font-medium">{m.categoria}{m.subcategoria && <span className="text-slate-500 text-xs ml-2 font-normal">({m.subcategoria})</span>}</td>
                                            <td className="px-6 py-4">{m.concepto}</td>
                                            <td className="px-6 py-4 font-mono font-medium">${Number(m.monto).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB: RETIROS */}
            {activeTab === 'retiros' && (
                <div className="space-y-6 animate-in fade-in duration-300">
                    <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden">
                        <div className="p-5 border-b border-slate-700/50">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2"><Wallet size={20} className="text-amber-400" /> Resumen de Saldos de Socios</h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-slate-300">
                                <thead className="bg-slate-900/50 text-slate-400 uppercase text-xs">
                                    <tr>
                                        <th className="px-6 py-4">Socio (Participación)</th>
                                        <th className="px-6 py-4 font-bold text-emerald-400 text-right">Ganancia Asignada</th>
                                        <th className="px-6 py-4 font-bold text-amber-400 text-right">Retiros Realizados</th>
                                        <th className="px-6 py-4 font-bold text-right">Saldo Pendiente</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/50">
                                    {socios.filter(s => s.estado?.toLowerCase() === 'activo').map(s => {
                                        const porcentaje = Number(s.porcentaje_participacion) || 0;
                                        const leToca = (stats.gananciaARepartir * (porcentaje / 100));
                                        const haRetirado = stats.retirosPorSocio[s.nombre] || 0;
                                        const saldoPendiente = leToca - haRetirado;
                                        
                                        let saldoColor = 'text-slate-400';
                                        if (saldoPendiente > 0) saldoColor = 'text-emerald-400';
                                        if (saldoPendiente < 0) saldoColor = 'text-red-400';

                                        return (
                                            <tr key={s.id} className="hover:bg-slate-700/30">
                                                <td className="px-6 py-4 font-bold text-white">{s.nombre} <span className="text-slate-500 font-normal text-xs ml-1">({porcentaje}%)</span></td>
                                                <td className="px-6 py-4 text-right font-mono text-emerald-400">${leToca.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                                <td className="px-6 py-4 text-right font-mono text-amber-400">${haRetirado.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                                <td className={`px-6 py-4 text-right font-mono font-bold text-lg ${saldoColor}`}>${saldoPendiente.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl p-6">
                        <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2"><Wallet size={20} className="text-amber-400" /> Registrar Nuevo Retiro</h3>
                        <form onSubmit={handleSaveRetiro} className="grid grid-cols-1 md:grid-cols-5 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">Socio</label>
                                <select value={socioRetiro} onChange={(e) => setSocioRetiro(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 transition-colors">
                                    {socios.filter(s => s.estado?.toLowerCase() === 'activo').map(s => <option key={s.id} value={s.nombre}>{s.nombre}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">Método</label>
                                <select value={metodoRetiro} onChange={(e) => setMetodoRetiro(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 transition-colors">
                                    {metodosRetiro.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">Monto ($)</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><DollarSign size={16} className="text-slate-500" /></div>
                                    <input type="text" inputMode="decimal" value={montoRetiro} onChange={(e) => setMontoRetiro(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-4 py-3 text-white focus:outline-none focus:border-amber-500 transition-colors placeholder-slate-600" placeholder="Ej. 10.000,05" required />
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-slate-400 mb-2">Descripción</label>
                                <div className="flex gap-2">
                                    <input type="text" value={conceptoRetiro} onChange={(e) => setConceptoRetiro(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 transition-colors placeholder-slate-600" placeholder="Ej: Retiro personal" required />
                                    <button type="submit" className="bg-amber-600 hover:bg-amber-500 text-white px-6 py-3 rounded-xl font-medium transition-colors whitespace-nowrap shadow-lg shadow-amber-500/20">Registrar</button>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden">
                        <div className="p-5 border-b border-slate-700/50"><h3 className="text-lg font-bold text-white">Historial de Retiros</h3></div>
                        <div className="overflow-x-auto max-h-[400px]">
                            <table className="w-full text-sm text-left text-slate-300">
                                <thead className="bg-slate-900/50 text-slate-400 uppercase text-xs sticky top-0">
                                    <tr><th className="px-6 py-4">Fecha</th><th className="px-6 py-4">Socio</th><th className="px-6 py-4">Método</th><th className="px-6 py-4">Descripción</th><th className="px-6 py-4">Monto</th><th className="px-6 py-4 text-center">Acciones</th></tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/50">
                                    {movimientos.filter(m => m.tipo === 'retiro').map(m => (
                                        <tr key={m.id} className="hover:bg-slate-700/30">
                                            <td className="px-6 py-4 text-xs text-slate-400">{formatDate(m.fecha)}</td>
                                            <td className="px-6 py-4 font-semibold text-white"><Users size={14} className="inline mr-2 text-amber-500" />{m.socio}</td>
                                            <td className="px-6 py-4">{m.categoria}</td>
                                            <td className="px-6 py-4">{m.concepto}</td>
                                            <td className="px-6 py-4 font-mono font-medium text-amber-400">${Number(m.monto).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                            <td className="px-6 py-4 text-center">
                                                <button onClick={() => handleEditRetiro(m)} className="text-slate-400 hover:text-indigo-400 mx-2 text-sm">Editar</button>
                                                <button onClick={() => handleDeleteRetiro(m.id)} className="text-slate-500 hover:text-red-400 text-sm">Eliminar</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB: FONDO DE RESERVA */}
            {activeTab === 'fondo' && (
                <div className="space-y-6 animate-in fade-in duration-300">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-xl">
                            <div className="flex justify-between items-start mb-4">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Fondo Total Asignado</span>
                                <div className="w-10 h-10 rounded-full bg-indigo-500/20 text-indigo-400 flex items-center justify-center"><Briefcase size={20} /></div>
                            </div>
                            <div className="text-3xl font-bold text-white">${stats.fondoTotalAsignado.toLocaleString(undefined, {minimumFractionDigits: 2})}</div>
                            <div className="text-xs text-slate-400 mt-2">Acumulado histórico</div>
                        </div>
                        <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-xl">
                            <div className="flex justify-between items-start mb-4">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Retirado</span>
                                <div className="w-10 h-10 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center"><TrendingDown size={20} /></div>
                            </div>
                            <div className="text-3xl font-bold text-white">${stats.totalRetirosFondo.toLocaleString(undefined, {minimumFractionDigits: 2})}</div>
                            <div className="text-xs text-slate-400 mt-2">Gastos/inversiones del fondo</div>
                        </div>
                        <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
                            <div className="flex justify-between items-start mb-4 relative z-10">
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Saldo Disponible</span>
                                <div className="w-10 h-10 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center"><Wallet size={20} /></div>
                            </div>
                            <div className="text-3xl font-bold text-white relative z-10">${stats.saldoFondo.toLocaleString(undefined, {minimumFractionDigits: 2})}</div>
                        </div>
                    </div>

                    <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl p-6">
                        <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2"><PlusCircle size={20} className="text-cyan-400" /> Registrar Retiro del Fondo</h3>
                        <form onSubmit={handleSaveRetiroFondo} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">Monto ($)</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><DollarSign size={16} className="text-slate-500" /></div>
                                    <input type="text" inputMode="decimal" value={montoFondo} onChange={(e) => setMontoFondo(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors placeholder-slate-600" placeholder="Ej. 10.000,05" required />
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-slate-400 mb-2">Motivo / Descripción</label>
                                <div className="flex gap-2">
                                    <input type="text" value={conceptoFondo} onChange={(e) => setConceptoFondo(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors placeholder-slate-600" placeholder="Ej: Compra de nuevos equipos" required />
                                    <button type="submit" className="bg-cyan-600 hover:bg-cyan-500 text-white px-6 py-3 rounded-xl font-medium transition-colors whitespace-nowrap shadow-lg shadow-cyan-500/20">Registrar</button>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden">
                        <div className="p-5 border-b border-slate-700/50"><h3 className="text-lg font-bold text-white">Historial de Uso del Fondo</h3></div>
                        <div className="overflow-x-auto max-h-[400px]">
                            <table className="w-full text-sm text-left text-slate-300">
                                <thead className="bg-slate-900/50 text-slate-400 uppercase text-xs sticky top-0">
                                    <tr><th className="px-6 py-4">Fecha</th><th className="px-6 py-4">Descripción</th><th className="px-6 py-4">Monto Retirado</th></tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/50">
                                    {retirosFondo.length === 0 ? (
                                        <tr><td colSpan="3" className="px-6 py-8 text-center text-slate-500">No hay retiros registrados.</td></tr>
                                    ) : (
                                        retirosFondo.map(r => (
                                            <tr key={r.id} className="hover:bg-slate-700/30">
                                                <td className="px-6 py-4 text-xs text-slate-400">{formatDate(r.fecha)}</td>
                                                <td className="px-6 py-4">{r.concepto}</td>
                                                <td className="px-6 py-4 font-mono font-medium text-amber-400">${Number(r.monto).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinanceDashboard;
